// Guest (not logged in) webshop basket helpers.
//
// The logged-in cart lives on the user record; anonymous visitors keep theirs
// in localStorage, one basket per customer (shop). Everything here is pure so
// the store and the drawer stay thin and the rules are unit-tested. Storage
// access is wrapped: a throwing localStorage (private mode, blocked site data)
// must read as an empty basket, never crash the page.

const MAX_QTY = 99;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const guestCartKey = customer => `guestCart-${customer}`;

const lineKey = line => `${line.id}|${line.color || ''}|${line.size || ''}`;

const isLine = line => line && typeof line === 'object' && Number.isInteger(line.id) && line.id > 0
    && Number.isInteger(line.qty) && line.qty > 0;

const readGuestCart = (storage, customer) => {
    try {
        if (!storage || typeof storage.getItem !== 'function') {
            return [];
        }
        const raw = storage.getItem(guestCartKey(customer));
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(isLine) : [];
    } catch (err) {
        return [];
    }
};

const writeGuestCart = (storage, customer, lines) => {
    try {
        if (!storage || typeof storage.setItem !== 'function') {
            return false;
        }
        storage.setItem(guestCartKey(customer), JSON.stringify(Array.isArray(lines) ? lines : []));
        return true;
    } catch (err) {
        return false;
    }
};

// The buyer leaves the page for Vipps (ePayment redirect) and comes back to
// it; the pending payment tells the store which order to look up on return.
const guestPendingKey = customer => `guestCartPending-${customer}`;

const readGuestPending = (storage, customer) => {
    try {
        if (!storage || typeof storage.getItem !== 'function') {
            return null;
        }
        const raw = storage.getItem(guestPendingKey(customer));
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed.paymentUuidv4 === 'string' && parsed.paymentUuidv4 ? parsed : null;
    } catch (err) {
        return null;
    }
};

const writeGuestPending = (storage, customer, pending) => {
    try {
        if (!storage || typeof storage.setItem !== 'function') {
            return false;
        }
        storage.setItem(guestPendingKey(customer), pending ? JSON.stringify(pending) : '');
        return true;
    } catch (err) {
        return false;
    }
};

const clampQty = qty => Math.min(MAX_QTY, Math.max(0, parseInt(qty, 10) || 0));

const addGuestLine = (lines, {
    product, qty = 1, color = '', size = '', race = 0,
}) => {
    const fresh = {
        id: product.id,
        name: product.name,
        price: product.price,
        vat: product.vat || 0,
        qty: Math.max(1, clampQty(qty)),
        color: color || '',
        size: size || '',
        race: race || 0,
        customer: product.customer,
        uuidv4: product.uuidv4,
        image: (product.images && product.images[0] && product.images[0].s3SmallLink) || '',
    };
    const key = lineKey(fresh);
    const existing = (lines || []).find(line => lineKey(line) === key);
    if (!existing) {
        return [...(lines || []), fresh];
    }
    return lines.map(line => (lineKey(line) === key
        ? { ...line, qty: Math.min(MAX_QTY, line.qty + fresh.qty) }
        : line));
};

const removeGuestLine = (lines, key) => (lines || []).filter(line => lineKey(line) !== key);

const setGuestQty = (lines, key, qty) => {
    const next = clampQty(qty);
    if (next <= 0) {
        return removeGuestLine(lines, key);
    }
    return (lines || []).map(line => (lineKey(line) === key ? { ...line, qty: next } : line));
};

const guestCartCount = lines => (lines || []).reduce((sum, line) => sum + (line.qty || 0), 0);

// Same rounding as backend lib/sale-invoice-html.js sumTotal(): whole kroner.
const guestCartTotal = lines => Math.round((lines || []).reduce((sum, line) => (
    sum + (line.price || 0) * (1 + (line.vat || 0) / 100) * (line.qty || 0)
), 0));

const toCheckoutLines = lines => (lines || []).map(line => ({
    id: line.id,
    qty: line.qty,
    color: line.color || '',
    size: line.size || '',
}));

const validateGuestForm = (form = {}) => {
    const errors = {};
    const text = value => (typeof value === 'string' ? value.trim() : '');
    if (!text(form.firstname)) {
        errors.firstname = true;
    }
    if (!text(form.lastname)) {
        errors.lastname = true;
    }
    if (text(form.cellphone).replace(/\D/g, '').length < 8) {
        errors.cellphone = true;
    }
    if (!EMAIL_RE.test(text(form.email))) {
        errors.email = true;
    }
    return errors;
};

export {
    MAX_QTY,
    guestCartKey,
    readGuestCart,
    writeGuestCart,
    lineKey,
    addGuestLine,
    setGuestQty,
    removeGuestLine,
    guestCartCount,
    guestCartTotal,
    toCheckoutLines,
    validateGuestForm,
    readGuestPending,
    writeGuestPending,
};
